import { useStorePlanningWeek, useStoreTodayActions } from '@gozoki/api'
import type { StorePlanningTask, StoreProcessTaskType } from '@gozoki/api-types'
import { useState } from 'react'
import bottomWaveGreen from '~/assets/dashboard/bottom-wave-green.svg'
import bottomWaveOrange from '~/assets/dashboard/bottom-wave-orange.svg'
import {
  ChevronRightBigIcon,
  CleanIcon,
  ClipboardIcon,
  MessageMultipleIcon,
  PackageDeliveredIcon,
  ShelvingUnitIcon,
  Truck24Icon,
} from './icons/dashboard-icons'
import { PointageModal } from './PointageModal'
import { TaskCompletionModal } from './TaskCompletionModal'

// Fin planifiée d'une tâche, en Date locale. `setHours` gère « 24:00 » → minuit du lendemain.
const taskEndsAt = (task: StorePlanningTask): Date => {
  const end = new Date(`${task.date}T00:00:00`)
  const [hours, minutes] = task.endTime.split(':').map(Number)
  end.setHours(hours, minutes, 0, 0)
  return end
}

const NEXT_TASK_ICON: Record<StoreProcessTaskType, typeof CleanIcon> = {
  CLEANING: CleanIcon,
  RECEPTION: PackageDeliveredIcon,
  LIVRAISON: Truck24Icon,
  INVENTORY: ShelvingUnitIcon,
  CLIENT_RELATION: MessageMultipleIcon,
}

// Figma 10715:59995 — les deux grands boutons d'action, hauteur 96.
// « Faire mon pointage » ouvre le wizard de pointage kiosk (POST /store/clock). « Valider la
// livraison » reste non branché (aucun créneau de réception en base).
export function BottomBar({
  storeLabel,
  storeAddress,
  storeDepartmentCode,
}: {
  storeLabel?: string
  storeAddress?: string | null
  storeDepartmentCode?: string | null
}) {
  const { data } = useStoreTodayActions()
  const lastClock = data?.lastClock ?? null
  const [isPointageOpen, setIsPointageOpen] = useState(false)
  const [isTaskOpen, setIsTaskOpen] = useState(false)

  // Prochaine tâche « process » à faire : la plus proche non terminée — on se cale sur l'heure de
  // FIN, pas de début, pour qu'une tâche EN COURS à l'instant T reste « à faire » (sinon le CTA
  // sautait la tâche du moment pour celle du lendemain). À défaut (tout est déjà terminé), la plus
  // ancienne non faite. Le CTA doré l'ouvre pour la valider.
  const { data: planning } = useStorePlanningWeek()
  const pendingTasks = (planning?.days ?? [])
    .flatMap((day) => day.tasks)
    .filter((task) => !task.completed)
    .sort((left, right) =>
      `${left.date}${left.startTime}`.localeCompare(`${right.date}${right.startTime}`),
    )
  const now = new Date()
  const nextTask = pendingTasks.find((task) => taskEndsAt(task) >= now) ?? pendingTasks[0] ?? null
  const nextTaskShifts = nextTask
    ? (planning?.days.find((day) => day.date === nextTask.date)?.shifts ?? [])
    : []
  const NextTaskIcon = nextTask ? NEXT_TASK_ICON[nextTask.type] : Truck24Icon

  return (
    <div className="flex h-[96px] shrink-0 items-stretch gap-[10px]">
      <button
        type="button"
        onClick={() => setIsPointageOpen(true)}
        className="relative flex min-w-0 flex-1 cursor-pointer items-center gap-[16px] overflow-hidden rounded-[24px] px-[24px] py-[24px] text-left"
        style={{
          backgroundImage: 'linear-gradient(160.65deg, #354A31 13.114%, #507247 61.367%)',
        }}
      >
        <img
          src={bottomWaveGreen}
          alt=""
          aria-hidden
          className="pointer-events-none absolute top-[-60px] right-[-40px] w-[583px] max-w-none rotate-180"
        />
        <span className="relative flex size-[48px] shrink-0 items-center justify-center rounded-[8px] bg-[#4F6A47] text-[#F9F8F1]">
          <ClipboardIcon />
        </span>
        <span className="relative shrink-0 font-brand text-[28px] leading-[32px] font-semibold whitespace-nowrap text-[#F9F8F1]">
          Faire mon pointage
        </span>
        <span className="relative min-w-0 flex-1 text-right font-sans text-[14px] leading-[16px] text-[#F0EDE2]">
          {lastClock ? (
            <>
              Dernier pointage :{' '}
              <span className="font-semibold">
                {lastClock.firstName}, {lastClock.clockedAt}
              </span>
            </>
          ) : (
            'Aucun pointage aujourd’hui'
          )}
        </span>
        <span className="relative shrink-0 text-[#F9F8F1]">
          <ChevronRightBigIcon />
        </span>
      </button>

      <button
        type="button"
        disabled={!nextTask}
        onClick={() => nextTask && setIsTaskOpen(true)}
        className="relative flex min-w-0 flex-1 cursor-pointer items-center gap-[16px] overflow-hidden rounded-[24px] p-[24px] text-left disabled:cursor-default"
        style={{
          backgroundImage: 'linear-gradient(-44.17deg, #E58C11 37.811%, #EFA031 62.189%)',
        }}
      >
        <img
          src={bottomWaveOrange}
          alt=""
          aria-hidden
          className="pointer-events-none absolute top-[-60px] right-[-40px] w-[583px] max-w-none"
        />
        <span className="relative flex size-[48px] shrink-0 items-center justify-center rounded-[8px] bg-[#F5C27A] text-[#F9F8F1]">
          <NextTaskIcon className="size-[28px]" />
        </span>
        <span className="relative shrink-0 font-brand text-[28px] leading-[32px] font-semibold whitespace-nowrap text-[#F9F8F1]">
          {nextTask ? nextTask.label : 'Tâches à faire'}
        </span>
        <span className="relative min-w-0 flex-1 text-right font-sans text-[14px] leading-[16px] text-[#F0EDE2]">
          {nextTask ? (
            <>
              Prochaine tâche :{' '}
              <span className="font-semibold">
                {nextTask.startTime} - {nextTask.endTime}
              </span>
            </>
          ) : (
            'Aucune tâche à faire'
          )}
        </span>
        <span className="relative shrink-0 text-[#F9F8F1]">
          <ChevronRightBigIcon />
        </span>
      </button>

      {isPointageOpen ? (
        <PointageModal
          storeLabel={storeLabel}
          storeAddress={storeAddress}
          storeDepartmentCode={storeDepartmentCode}
          onClose={() => setIsPointageOpen(false)}
        />
      ) : null}

      {isTaskOpen && nextTask ? (
        <TaskCompletionModal
          task={nextTask}
          shifts={nextTaskShifts}
          onClose={() => setIsTaskOpen(false)}
        />
      ) : null}
    </div>
  )
}
